import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SkeletonLoader from "../components/skeletonLoader";

const SessionHistory = ({ dir }) => {

  const [selectedSession, setSelectedSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isDev = process.env.NODE_ENV === 'development';

  // Get sidebar state from CSS custom property
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Add this useEffect for sidebar state management
  useEffect(() => {
    const updateSidebarState = () => {
      const sidebarState = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-open');
      setSidebarOpen(sidebarState === '1');
    };

    updateSidebarState();

    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    const observer = new MutationObserver(updateSidebarState);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_BASEURL;
      if (isDev) console.log("Fetching sessions from:", `${baseUrl}/get_user_sessions`);

      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${baseUrl}/get_user_sessions`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });

      if (isDev) console.log("API response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        if (isDev) console.error("Error response:", errorText);
        throw new Error(t("error_fetching_sessions")` ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const validSessions = Array.isArray(data.sessions) ? data.sessions : [];

      const sanitizedSessions = validSessions.map(session => ({
        id: session.sessionId || session._id || `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: session.chatHistory && session.chatHistory[0] && session.chatHistory[0].question
          ? session.chatHistory[0].question
          : "Unnamed Session",
        date: session.date || new Date().toISOString(),
        last_message: session.chatHistory && session.chatHistory.length > 1
          ? session.chatHistory[session.chatHistory.length - 1].question
          : "",
        chatHistory: session.chatHistory || [],
        sessionTitle: session.sessionTitle || session?.chatHistory[session?.chatHistory?.length - 1]?.question,
      }));
      setSessions(sanitizedSessions);
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [isDev, t]);

  const handleDeleteSession = async (sessionId) => {
    const confirmDelete = window.confirm(t("are_you_sure_delete_session"));
    if (!confirmDelete) return;

    const baseUrl = process.env.REACT_APP_BASEURL;
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    try {
      const response = await fetch(`${baseUrl}/delete_user_sessions?sessionId=${sessionId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error deleting session:", errorText);
        throw new Error(t("failed_delete_session"));
      }

      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (selectedSession === sessionId) setSelectedSession(null);
    } catch (error) {
      console.error("Delete error:", error);
      alert(t("failed_to_delete_session_desc"));
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const convertChatHistoryToConversation = (chatHistory) => {
    const conversation = [];

    if (!chatHistory || !Array.isArray(chatHistory)) {
      console.error("Invalid chat history:", chatHistory);
      return conversation;
    }

    const baseTimestamp = Date.now();

    chatHistory.forEach((entry, index) => {
      if (entry.question) {
        conversation.push({
          id: `user-${baseTimestamp}-${index}`,
          sender: "user",
          message: entry.question
        });
      }

      if (entry.answer) {
        let cleanedAnswer = entry.answer;
        const referencesPattern = /\*\*References:\*\*([\s\S]*?)(?:\*\*followUp:|$)/;
        cleanedAnswer = cleanedAnswer.replace(referencesPattern, "");

        const followUpPattern = /\*\*followUp:\*\*([\s\S]*?)(?:$)/;
        cleanedAnswer = cleanedAnswer.replace(followUpPattern, "");

        const questionPatterns = [
          /\*\* Question:[\s\S]*?(?=\*\*|\n\n|$)/g,
          /\*\*Question:[\s\S]*?(?=\*\*|\n\n|$)/g,
          /\*\*\s*Question:[\s\S]*?(?=\*\*|\n\n|$)/g
        ];

        for (const pattern of questionPatterns) {
          cleanedAnswer = cleanedAnswer.replace(pattern, "");
        }

        cleanedAnswer = cleanedAnswer.trim();

        conversation.push({
          id: `ai-${baseTimestamp}-${index}`,
          sender: "ai",
          message: cleanedAnswer,
          ratingGiven: null
        });
      }
    });

    return conversation;
  };

  const handleShowSession = async () => {
    if (!selectedSession) {
      alert("Please select a session.");
      return;
    }

    setLoading(true);

    try {
      const selectedSessionData = sessions.find(s => s.id === selectedSession);

      if (selectedSessionData && selectedSessionData.chatHistory && selectedSessionData.chatHistory.length > 0) {
        if (isDev) console.log("Using cached session data");

        const conversation = convertChatHistoryToConversation(selectedSessionData.chatHistory);

        sessionStorage.setItem("sessionNumber", selectedSession);
        sessionStorage.setItem("conversation", JSON.stringify(conversation));

        navigate("/");
        return;
      }

      const baseUrl = process.env.REACT_APP_BASEURL || "https://3dflg5cs-8000.inc1.devtunnels.ms";
      const url = `${baseUrl}/get_user_sessions?sessionId=${selectedSession}`;
      if (isDev) console.log("Fetching session details from:", url);

      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });

      if (isDev) console.log("Session details response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        if (isDev) console.error("Error response for session details:", errorText);
        throw new Error(t("error_fetching_sessions_details")` ${response.status} ${response.statusText}`);
      }

      const sessionData = await response.json();

      if (!sessionData) {
        throw new Error(t("invalid_session_data"));
      }

      let chatHistory = [];

      if (sessionData.chatHistory) {
        chatHistory = sessionData.chatHistory;
      } else if (sessionData.sessions && sessionData.sessions[0] && sessionData.sessions[0].chatHistory) {
        chatHistory = sessionData.sessions[0].chatHistory;
      }

      if (!chatHistory || chatHistory.length === 0) {
        throw new Error(t("No chat history found"));
      }

      const conversation = convertChatHistoryToConversation(chatHistory);

      sessionStorage.setItem("currentSessionId", selectedSession);
      sessionStorage.setItem("conversation", JSON.stringify(conversation));

      navigate("/");
    } catch (err) {
      console.error("Failed to fetch session details:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredSessions = sessions.filter((session) =>
    session && session.name &&
    session.name.toLowerCase().includes((searchQuery || "").toLowerCase())
  );

  const truncateText = (text, maxLength = 30) => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  // Calculate responsive padding based on sidebar state
  const getContentPadding = () => {
    if (isMobile) {
      return "";
    }
    return sidebarOpen
      ? (dir === 'rtl' ? 'pr-64' : 'pl-64')
      : (dir === 'rtl' ? 'pr-20' : 'pl-20');
  };

  return (
    <div className={`transition-all duration-300 ${getContentPadding()}`}>

      <div className="flex justify-between items-center mb-10 mt-4 mx-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/lms/courses")} style={{ backgroundColor: "#767676" }} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center transition-all duration-300 cursor-pointer">
            <i className="ri-arrow-left-line text-xl text-white" />
          </button>
          <div>
            <h2 className="text-xl font-medium text-gray-900">
              {t("session_history")}
            </h2>
          </div>
        </div>
        <div dir={dir} className="relative w-[300px]">
          <input
            type="text"
            placeholder={t("Search session history")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value.replace(/\s{2,}/g, " "))}
            className="w-full border border-gray-300 rounded-md py-3 px-4 text-gray-700 focus:ring-2 focus:ring-[#A73884] outline-none"
          />
        </div>
      </div>

      {/* Session List */}
      <div
        dir={dir}
        className="divide-y divide-gray-200 overflow-y-auto rounded-lg mx-4"
        style={{height: "calc(100vh - 270px)"}}
      >
        {loading ? (
          [1, 2, 3, 4, 5, 6].map((i) => (
            <SkeletonLoader key={i} count={1} height={44} className="mb-2" />
          ))
        ) : filteredSessions.length > 0 ? (
          filteredSessions.map((session) => (
            <div
              key={session.id}
              onClick={() => setSelectedSession(session.id)}
              className={`flex items-center justify-between py-3 px-4 cursor-pointer rounded-lg transition 
                ${selectedSession === session.id
                  ? "bg-gradient-to-r from-[#A73884] to-[#803A85] text-white"
                  : "hover:bg-gray-100"
                }`}
            >
              <span
                className={`font-medium text-sm sm:text-base ${selectedSession === session.id ? "text-white" : "text-gray-700"
                  }`}
              >
                {truncateText(session.sessionTitle, 50)}
              </span>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteSession(session.id);
                }}
                className="text-red-500 hover:text-red-700 text-3xl font-bold ml-4"
                title={t("Delete session")}
              >
                &times;
              </button>
            </div>
          ))
        ) : (
          <p className="text-center text-gray-500 py-4">{t("no_sessions_found")}</p>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-center sm:space-x-4 mt-8 gap-4">
        <button
          className={`px-6 py-3 bg-transparent border-2 border-[#B03982] text-[#B03982] rounded-full 
            transition w-full sm:w-48
            ${loading || !selectedSession
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-[#B03982] hover:text-white"
            }`}
          onClick={handleShowSession}
          disabled={loading || !selectedSession}
        >
          {loading ? t("loading") : t("show_session")}
        </button>

        {/* <button
          className="px-6 py-3 bg-[#B03982] text-white rounded-full w-full sm:w-48 hover:bg-[#8c2d66] transition"
          onClick={() => navigate("/")}
          disabled={loading}
        >
          {t("back_to_home")}
        </button> */}
      </div>
    </div>
  );
};

export default SessionHistory;
